import { Badge, Button, Card, EmptyState, LoadingRows, PageHeader } from '@shared/ui';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useRefusals, useRefusalSummary } from '../hooks/use-refusals';
import {
    isRollingWindow,
    parseFilters,
    serializeFilters,
    toRequestFilters,
} from '../model/filters';

import { ExportButton } from './ExportButton';
import { FailurePanel } from './FailurePanel';
import { RefusalDetail } from './RefusalDetail';
import { RefusalsFilters } from './RefusalsFilters';
import { RefusalsSummary } from './RefusalsSummary';
import { RefusalsTable } from './RefusalsTable';
import { ScopeNotice } from './ScopeNotice';

import type { ViewerFilters } from '../model/filters';
import type { RefusalRecord } from '../schemas/refusals';

const ALERT_CONTEXT = {
    'refusals-domain': 'Opened from an alert: refusals of one domain',
    'refusals-platform': 'Opened from an alert: platform-wide refusals',
} as const;

/**
 * Sign-in refusals no tenant could be named for (INI-0313, D1).
 *
 * The URL is the only source of the query: an alert's link opens the screen
 * already filtered, and a copied link reproduces it. Without a window it means
 * the last 24 hours as of when the page opened, or of the last "Refresh".
 */
export function UnidentifiedRefusalsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
    const [now, setNow] = useState(() => new Date());
    const request = useMemo(() => toRequestFilters(filters, now), [filters, now]);
    const [open, setOpen] = useState<RefusalRecord | null>(null);

    const list = useRefusals(request);
    const summary = useRefusalSummary(request);

    const apply = (next: ViewerFilters) => {
        setNow(new Date());
        setSearchParams(serializeFilters(next));
    };

    /**
     * Advancing `now` only changes the request — and so only refetches —
     * for the rolling window; a fixed one (an alert link, or a custom range)
     * ignores `now` entirely (`toRequestFilters`), so "Refresh" would
     * otherwise be a silent no-op for the one case it most needs to work:
     * reopening an alert's link to see whether anything new has come in.
     */
    const refresh = () => {
        setNow(new Date());
        if (!isRollingWindow(filters)) {
            void list.refetch();
            void summary.refetch();
        }
    };

    const records = list.data?.pages.flatMap((page) => page.records) ?? [];
    const failure = list.error ?? summary.error;

    return (
        <div className="max-w-[1248px]">
            <PageHeader
                eyebrow="Security"
                title="Unidentified refusals"
                description="Institutional sign-ins refused before any museum could be identified."
                actions={
                    <Button variant="ghost" onClick={refresh}>
                        Refresh
                    </Button>
                }
            />
            <ScopeNotice />
            {filters.type !== undefined && (
                <div className="mb-4">
                    <Badge tone="info">{ALERT_CONTEXT[filters.type]}</Badge>
                </div>
            )}
            <Card className="mb-5">
                <RefusalsFilters
                    // Remounted on a new URL so the draft starts from what is applied.
                    key={searchParams.toString()}
                    filters={filters}
                    onApply={apply}
                    exportControl={<ExportButton filters={request} />}
                />
            </Card>
            {failure ? (
                <Card>
                    <FailurePanel
                        error={failure}
                        failedAt={Math.max(list.errorUpdatedAt, summary.errorUpdatedAt)}
                        onRetry={() => {
                            if (list.error) void list.refetch();
                            if (summary.error) void summary.refetch();
                        }}
                    />
                </Card>
            ) : (
                <>
                    {summary.data ? (
                        <RefusalsSummary summary={summary.data} window={request} />
                    ) : (
                        <Card className="mb-5">
                            <LoadingRows label="Loading the summary…" />
                        </Card>
                    )}
                    <Card>
                        {list.isPending ? (
                            <LoadingRows label="Loading refusals…" />
                        ) : records.length === 0 ? (
                            <EmptyState title="There are no records" />
                        ) : (
                            <RefusalsTable
                                records={records}
                                hasMore={list.hasNextPage}
                                loadingMore={list.isFetchingNextPage}
                                onLoadMore={() => void list.fetchNextPage()}
                                onOpen={setOpen}
                            />
                        )}
                    </Card>
                </>
            )}
            <RefusalDetail record={open} onClose={() => setOpen(null)} />
        </div>
    );
}
